<script lang="ts">
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu";
  import { Share2, Lock, Link2, CheckCircle2 } from "lucide-svelte";
  import { SearchAPI } from "$lib/api/search";
  import { onMount } from "svelte";
  import { Button } from "$lib/components/ui/button";
  import { cn } from "$lib/utils";
  import { Portal } from "bits-ui";
  // import { toast } from "$lib/components/ui/toast";

  let {
    searchId,
    isPublic = $bindable(false),
    disabled = false,
    className = "",
  } = $props<{
    searchId: string;
    isPublic: boolean;
    disabled?: boolean;
    className?: string;
  }>();

  let isOpen = $state(false);
  let isUpdating = $state(false);
  let justCopied = $state(false);

  async function handleTogglePublic(newPublicState: boolean) {
    isUpdating = true;
    try {
      await SearchAPI.updateSearchPublicStatus(searchId, newPublicState);
      isPublic = newPublicState;

      if (newPublicState) {
        handleCopyLink();
      }
    } catch (error) {
      // toast.error("Failed to update sharing status");
      console.error("Failed to update public status:", error);
    } finally {
      isUpdating = false;
    }
  }

  async function handleCopyLink() {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      justCopied = true;
      // toast.success("Link copied to clipboard");
      setTimeout(() => {
        justCopied = false;
      }, 2000);
    } catch (error) {
      // toast.error("Failed to copy link");
      console.error("Failed to copy link:", error);
    }
  }

  onMount(() => {
    return () => {
      isOpen = false;
      isUpdating = false;
      justCopied = false;
    };
  });
</script>

<DropdownMenu.Root open={isOpen} onOpenChange={(open) => (isOpen = open)}>
  <DropdownMenu.Trigger disabled={disabled || isUpdating} class="inline-flex items-center justify-center">
    <Button
      class={cn(
        "justift-center flex h-fit items-center gap-1.5 !rounded-base bg-black px-1.5 py-1 text-2xs font-normal text-white dark:border-white dark:border-white/20 dark:bg-theme-green dark:text-black dark:hover:text-black",
        className,
      )}
      aria-label="Share search"
      {disabled}
    >
      <Share2 strokeWidth={1} class="max-w-4" />
      Share
    </Button>
  </DropdownMenu.Trigger>

  <Portal>
    <DropdownMenu.Content
      side="bottom"
      align="start"
      sideOffset={5}
      class="w-[300px] !rounded-base border border-gray-200 bg-theme-background-primary p-2 shadow-sm dark:border-white/20"
    >
      <!-- Secret Option -->
      <DropdownMenu.Item
        class="flex w-full cursor-pointer items-center justify-between !rounded-base px-3 py-1 outline-none hover:cursor-pointer hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-white/20"
        disabled={isUpdating}
        onclick={() => handleTogglePublic(false)}
      >
        <div class="flex items-center gap-4">
          <Lock strokeWidth={1} class="h-4 w-4" />
          <div class="space-y-1">
            <h3 class="text-sm font-medium text-theme-text-primary">Private</h3>
            <p class="text-xs text-theme-text-secondary">Only you can view</p>
          </div>
        </div>
        {#if !isPublic}
          <CheckCircle2 strokeWidth={1} class="text-theme-black h-4 w-4 dark:text-theme-green" />
        {/if}
      </DropdownMenu.Item>

      <!-- Shareable Option -->
      <DropdownMenu.Item
        class="mt-1 flex w-full cursor-pointer items-center justify-between !rounded-base px-3 py-1 outline-none hover:cursor-pointer hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-white/20"
        disabled={isUpdating}
        onclick={() => handleTogglePublic(true)}
      >
        <div class="flex items-center gap-4">
          <Link2 strokeWidth={1} class="h-4 w-4" />
          <div class="space-y-1">
            <h3 class="text-sm font-medium text-theme-text-primary">Shareable</h3>
            <p class="text-xs text-theme-text-secondary">Anyone with the link can view</p>
          </div>
        </div>
        {#if isPublic}
          <CheckCircle2 strokeWidth={1} class="text-theme-black h-4 w-4 dark:text-theme-green" />
        {/if}
      </DropdownMenu.Item>

      {#if isPublic}
        <div class="mt-2 px-3 py-2">
          <button
            class="flex w-full items-center justify-center gap-2 rounded-md border border-input bg-transparent px-3 py-0.5 text-2xs font-normal text-theme-text-primary shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground hover:text-theme-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            onclick={handleCopyLink}
          >
            <Link2 strokeWidth={1} class="h-4 w-4" />
            {justCopied ? "Link copied!" : "Copy Link"}
          </button>
        </div>
      {/if}
    </DropdownMenu.Content>
  </Portal>
</DropdownMenu.Root>
